import React, { useState, useEffect } from "react";
import config from '../config/config';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Grid, Alert, MenuItem, Select,
  InputLabel, FormControl, CircularProgress, Box, IconButton,
} from "@mui/material";
import { Close, Payment } from "@mui/icons-material";

const T = {
  white: '#ffffff',
  bg: '#f8f9fc',
  border: '#e8ecf4',
  borderLight: '#f0f3fb',
  accent: '#5b4fcf',
  accentLight: '#ede9fd',
  accentMid: '#7c6fe0',
  text: '#1a1d2e',
  textSub: '#6b7280',
  textMuted: '#9ca3af',
  success: '#059669',
  successBg: '#ecfdf5',
  error: '#dc2626',
  errorBg: '#fef2f2',
  warning: '#d97706',
  shadow: '0 4px 24px rgba(0,0,0,0.08)',
  radius: '18px',
};

const pillBtn = (variant = 'accent') => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: 'Quicksand, sans-serif',
  fontSize: '13px',
  px: 2.5,
  py: 1,
  transition: 'all 0.18s ease',
  ...(variant === 'accent' && {
    background: T.accent,
    color: '#fff',
    border: 'none',
    '&:hover': { background: T.accentMid, boxShadow: '0 4px 14px rgba(91,79,207,0.35)' },
    '&.Mui-disabled': { background: '#c4c4c4', color: '#fff' },
  }),
  ...(variant === 'ghost' && {
    background: T.bg,
    color: T.textSub,
    border: `1px solid ${T.border}`,
    '&:hover': { background: T.border },
  }),
});

const styledInput = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: 'Quicksand, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    background: T.white,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.accent, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: '13px' },
  '& .MuiInputLabel-root.Mui-focused': { color: T.accent },
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(amount || 0);

const PaymentModal = ({ open, onClose, invoice, invoiceItems, accounts, onSubmit }) => {
  const [transactionCode, setTransactionCode] = useState("");
  const [excessAmounts, setExcessAmounts] = useState({});
  const [paymentInputs, setPaymentInputs] = useState({});
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && invoiceItems?.length) {
      const initialInputs = {};
      const initialExcess = {};
      invoiceItems.forEach((item) => {
        initialInputs[item.id] = "0.00";
        initialExcess[item.id] = 0;
      });
      setPaymentInputs(initialInputs);
      setExcessAmounts(initialExcess);
      setTransactionCode("");
      setPaymentMode("");
      setPaymentNote("");
      setErrors({});
      setMessage(null);
      setLoading(false);
    }
  }, [open, invoiceItems]);

  const validateInputs = () => {
    const newErrors = {};
    let hasError = false;
    if (!transactionCode.trim()) { newErrors.transactionCode = "Transaction code is required"; hasError = true; }
    if (!paymentMode) { newErrors.paymentMode = "Please select a payment mode"; hasError = true; }
    invoiceItems.forEach((item) => {
      const val = paymentInputs[item.id];
      if (val === "" || val === undefined) { newErrors[item.id] = "Amount is required"; hasError = true; }
      else if (isNaN(val)) { newErrors[item.id] = "Amount must be a number"; hasError = true; }
      else if (parseFloat(val) < 0) { newErrors[item.id] = "Amount cannot be negative"; hasError = true; }
    });
    setErrors(newErrors);
    return !hasError;
  };

  const handleInputChange = (itemId, maxAmount, value) => {
    if (value === "") {
      setPaymentInputs((prev) => ({ ...prev, [itemId]: "" }));
      setExcessAmounts((prev) => ({ ...prev, [itemId]: 0 }));
      setErrors((prev) => ({ ...prev, [itemId]: null }));
      return;
    }
    if (!/^\d*\.?\d*$/.test(value)) { setErrors((prev) => ({ ...prev, [itemId]: "Invalid amount" })); return; }
    const amount = parseFloat(value);
    if (isNaN(amount) || amount < 0) { setErrors((prev) => ({ ...prev, [itemId]: "Invalid amount" })); return; }
    const excess = amount > maxAmount ? (amount - maxAmount).toFixed(2) : 0;
    setExcessAmounts((prev) => ({ ...prev, [itemId]: excess }));
    setPaymentInputs((prev) => ({ ...prev, [itemId]: value }));
    setErrors((prev) => ({ ...prev, [itemId]: null }));
  };

  const handleSave = async () => {
    setMessage(null);
    if (!validateInputs()) { setMessage({ type: "error", text: "Please fix validation errors." }); return; }
    const hasExcess = Object.values(excessAmounts).some((excess) => parseFloat(excess) > 0);
    if (hasExcess) {
      const proceed = window.confirm("Some items have excess payments. Do you want to continue?");
      if (!proceed) return;
    }
    const payload = {
      invoice_id: invoice,
      transaction_code: transactionCode,
      payment_mode: paymentMode,
      payment_note: paymentNote,
      payments: Object.entries(paymentInputs).map(([itemId, amount]) => ({ item_id: itemId, amount_paid: parseFloat(amount) })),
    };
    try {
      setLoading(true);
      const response = await onSubmit(payload);
      if (response.success) { setMessage({ type: "success", text: response.message || "Payment saved successfully." }); }
      else { setMessage({ type: "error", text: response.message || "Failed to save payment." }); }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "An error occurred." });
    } finally {
      setLoading(false);
    }
  };

  const totalPaying = Object.values(paymentInputs).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{
      sx: {
        borderRadius: T.radius,
        border: `1px solid ${T.border}`,
        boxShadow: T.shadow,
        fontFamily: 'Quicksand, sans-serif',
        background: T.white,
      }
    }}>
      <DialogTitle sx={{ pb: 2, borderBottom: `1px solid ${T.border}`, px: 3.5, pt: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: T.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Payment sx={{ fontSize: 20, color: T.accent }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: T.text, fontFamily: 'Quicksand, sans-serif' }}>
                Add Payment
              </Typography>
              <Typography sx={{ fontSize: '12px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
                Invoice #{invoice?.invoiceNumber || invoice?.invoice_number || invoice}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3.5, py: 3 }}>
        {message && (
          <Alert severity={message.type} sx={{ mb: 2.5, borderRadius: '10px', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
            {message.text}
          </Alert>
        )}

        <Grid container spacing={2.5} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Transaction Code"
              value={transactionCode}
              onChange={(e) => setTransactionCode(e.target.value)}
              disabled={loading}
              error={Boolean(errors.transactionCode)}
              helperText={errors.transactionCode}
              fullWidth
              sx={styledInput}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(errors.paymentMode)} sx={styledInput}>
              <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
              <Select
                labelId="payment-mode-label"
                value={paymentMode}
                label="Payment Mode"
                onChange={(e) => setPaymentMode(e.target.value)}
                disabled={loading}
              >
                {accounts.length > 0 && accounts.map(account => (
                  <MenuItem key={account.type} value={account.type} sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: '13px' }}>
                    {account.name} ({account.balance})
                  </MenuItem>
                ))}
              </Select>
              {errors.paymentMode && (
                <Typography variant="caption" color="error" sx={{ fontFamily: 'Quicksand, sans-serif', mt: 0.5, ml: 1.5 }}>
                  {errors.paymentMode}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ my: 2.5, height: '1px', background: T.border }} />

        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: T.text, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Payment Amounts
        </Typography>

        {invoiceItems?.length ? invoiceItems.map((item) => {
          const balance = parseFloat(item.total) - parseFloat(item.paid || 0);
          return (
            <Box key={item.id} sx={{ background: T.bg, borderRadius: '12px', border: `1px solid ${T.borderLight}`, px: 2.5, py: 2, mb: 1.5 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: T.text, fontFamily: 'Quicksand, sans-serif' }}>
                    {item.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
                      Total: <strong style={{ color: T.text }}>{formatCurrency(item.total)}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
                      Balance: <strong style={{ color: balance > 0 ? T.error : T.success }}>{formatCurrency(balance)}</strong>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Amount Paying"
                    fullWidth
                    type="tel"
                    inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                    value={paymentInputs[item.id] ?? ""}
                    placeholder={balance.toFixed(2)}
                    onChange={(e) => handleInputChange(item.id, balance, e.target.value)}
                    error={Boolean(errors[item.id])}
                    helperText={errors[item.id]}
                    disabled={loading}
                    sx={styledInput}
                  />
                  {excessAmounts[item.id] > 0 && (
                    <Box sx={{ mt: 0.8, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', px: 1.5, py: 0.6 }}>
                      <Typography sx={{ fontSize: '12px', color: T.warning, fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                        ⚠ Excess: {formatCurrency(excessAmounts[item.id])}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          );
        }) : (
          <Typography sx={{ color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>No invoice items found.</Typography>
        )}

        <Box sx={{ my: 2.5, height: '1px', background: T.border }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box sx={{ background: T.accentLight, borderRadius: '12px', px: 2.5, py: 1.5, border: `1px dashed #c4bcf7` }}>
            <Typography sx={{ fontSize: '12px', color: T.accent, fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}>
              Total Being Paid
            </Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: T.accent, fontFamily: 'Quicksand, sans-serif', lineHeight: 1.2 }}>
              {formatCurrency(totalPaying)}
            </Typography>
          </Box>
        </Box>

        <TextField
          label="Payment Note (optional)"
          multiline
          rows={3}
          fullWidth
          value={paymentNote}
          onChange={(e) => setPaymentNote(e.target.value)}
          disabled={loading}
          sx={styledInput}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3.5, pb: 3.5, pt: 1, gap: 1.5 }}>
        <Button onClick={onClose} disabled={loading} sx={pillBtn('ghost')}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Payment sx={{ fontSize: 16 }} />}
          sx={pillBtn('accent')}
        >
          {loading ? "Saving..." : "Save Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;